import {
  FEATURE_DEFAULTS,
  type FeatureBillingTier,
  ProjectFeature,
  type SubscriptionFeatureConfig,
} from '@civilgrid/utils/src/common/featureAccess';
import { hasFeatureAccess } from '~/authorizations/FeatureFlags';

export type TopNavGateInputs = {
  projectId: string;
  project: { id: string; billingTier: FeatureBillingTier } | undefined;
  subscription: SubscriptionFeatureConfig | null | undefined;
  orgResolved: boolean;
  isError: boolean;
  isPageRedesignEnabled: boolean;
};

export type TopNavGates = {
  ready: boolean;
  showDownloads: boolean;
  showDrawerButtons: boolean;
  showContacts: boolean;
  showExports: boolean;
  showReports: boolean;
  /** Downloads is in this tier's plan but an admin turned it off for the org, so the menu hides it. */
  downloadsTurnedOff: boolean;
};

/**
 * Resolves every gate the utilities top nav needs in one pass so the action buttons can render as a
 * single group instead of appearing one input at a time and shifting each other sideways.
 *
 * `ready` is false until the project and the org (which carries the subscription) have both landed;
 * `isError` releases it so a failed fetch doesn't leave the bar in its loading state forever.
 *
 * CivilSearch and Downloads both go through `hasFeatureAccess`. Neither has a legacy org flag, so for
 * CivilSearch this matches the store's `isFullUtilitiesExperienceEnabled` getter exactly.
 *
 * `showContacts` needs the page redesign flag and follows `UtilitiesReports`, the gate on the Reports subtab
 * that holds Contacts while the flag is off.
 *
 * With the page redesign flag on, the Exports menu shows when Downloads or Reports is available; `showDownloads` and
 * `showReports` say which, so the menu can disable the rest.
 */
export function getTopNavGates({
  projectId,
  project,
  subscription,
  orgResolved,
  isError,
  isPageRedesignEnabled,
}: TopNavGateInputs): TopNavGates {
  const projectResolved = project?.id === projectId;
  const ready = isError || (projectResolved && orgResolved);

  if (!ready || !project || !projectResolved) {
    return {
      ready,
      showDownloads: false,
      showDrawerButtons: false,
      showContacts: false,
      showExports: false,
      showReports: false,
      downloadsTurnedOff: false,
    };
  }

  const showDownloads = hasFeatureAccess(ProjectFeature.Downloads, project.billingTier, subscription);
  const hasReports = hasFeatureAccess(ProjectFeature.UtilitiesReports, project.billingTier, subscription);

  return {
    ready,
    showDownloads,
    showDrawerButtons: hasFeatureAccess(ProjectFeature.CivilSearch, project.billingTier, subscription),
    showContacts: isPageRedesignEnabled && hasReports,
    showExports: isPageRedesignEnabled && (showDownloads || hasReports),
    showReports: hasReports,
    // Off on a tier whose plan includes it means an admin override; off on Lite is the plan, which stays an upsell.
    downloadsTurnedOff: !showDownloads && FEATURE_DEFAULTS[ProjectFeature.Downloads].includes(project.billingTier),
  };
}
